import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAdminProducts, deleteProduct, getCategories, Product, Category, ProductFilters } from '../api/products.api';
import LoadingSpinner from '../components/LoadingSpinner';
import DeleteProductModal from '../components/DeleteProductModal';
import ProductImage from '../components/ProductImage';

const AdminProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null); // null = all, true = active, false = inactive
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; productId: string | null; productName: string }>({
    show: false,
    productId: null,
    productName: '',
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const filters: ProductFilters = {
        page: currentPage,
        limit: 25,
      };

      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      if (categoryFilter) {
        filters.category = categoryFilter;
      }

      if (statusFilter !== null) {
        filters.is_active = statusFilter;
      }

      const response = await getAdminProducts(filters);
      console.log('Products API Response:', response);
      
      // Response structure: response is ApiResponse<ProductsResponse>
      // response.data is ProductsResponse which contains { data: Product[], pagination: {...} }
      const productsResponse = response.data;
      console.log('Products Response Data:', productsResponse);
      
      // ProductsResponse has { data: Product[], pagination: {...} }
      const products = Array.isArray(productsResponse.data) ? productsResponse.data : [];
      const pagination = productsResponse.pagination || { total_pages: 1, total: 0 };
      
      console.log('Extracted Products:', products);
      console.log('Pagination:', pagination);
      
      setProducts(products);
      setTotalPages(pagination.total_pages || 1);
      setTotalProducts(pagination.total || 0);
    } catch (err: any) {
      console.error('Error fetching products:', err);
      console.error('Error details:', err.response?.data);
      setProducts([]);
      setError(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, categoryFilter, statusFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await getCategories();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchProducts();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setStatusFilter(null);
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    if (!deleteModal.productId) return;

    try {
      await deleteProduct(deleteModal.productId);
      setDeleteModal({ show: false, productId: null, productName: '' });
      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const isClearDisabled = !searchQuery.trim() && !categoryFilter && statusFilter === null;

  const getStatusBadgeClass = (isActive: boolean) => {
    return isActive ? 'bg-success' : 'bg-secondary';
  };

  if (loading && products.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">PRODUCT MANAGEMENT</h1>
          <p className="text-muted-soft mb-0">Search, view, and manage all products</p>
        </div>
        <Link to="/dashboard/admin/products/new" className="btn btn-arena">
          <i className="bi bi-plus-circle me-2"></i>
          Add Product
        </Link>
      </div>

      <div className="card card-surface mb-4">
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">Search Products</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, category, or price..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setCurrentPage(1);
                    }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter === null ? '' : statusFilter.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  setStatusFilter(value === '' ? null : value === 'true');
                  setCurrentPage(1);
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="col-12 d-flex justify-content-between align-items-center">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleClearFilters}
                disabled={isClearDisabled}
              >
                Clear Filters
              </button>
              <button className="btn btn-arena btn-sm" onClick={handleSearch}>
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card card-surface">
        <div className="card-body">
          {error && (
            <div className="alert alert-danger mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}
          {products.length === 0 && !loading ? (
            <div className="alert alert-secondary mb-0">
              {error ? 'Failed to load products. Check console for details.' : 'No products found.'}
            </div>
          ) : (
            <>
              <div className="small text-muted-soft mb-2">
                Showing {products.length} of {totalProducts} results
              </div>

              {/* Desktop Table View */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Sizes</th>
                      <th>Stock</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id}>
                        <td>
                          <ProductImage
                            src={product.image_url}
                            alt={product.name}
                            height={60}
                            width={60}
                            style={{ borderRadius: '4px' }}
                          />
                        </td>
                        <td className="fw-semibold">{product.name}</td>
                        <td>{product.category?.name || 'N/A'}</td>
                        <td>BDT {product.price.toFixed(2)}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {product.available_sizes.map((size) => (
                              <span key={size} className="badge bg-secondary">
                                {size}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>{product.stock_quantity ?? 'N/A'}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(product.is_active)} badge-status`}>
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(`/dashboard/admin/products/${product.id}/edit`)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                setDeleteModal({
                                  show: true,
                                  productId: product.id,
                                  productName: product.name,
                                })
                              }
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {products.map((product) => (
                  <div key={product.id} className="card card-surface mb-3">
                    <div className="card-body">
                      <div className="d-flex gap-3 mb-3">
                        <ProductImage
                          src={product.image_url}
                          alt={product.name}
                          height={80}
                          width={80}
                          style={{ borderRadius: '4px', flexShrink: 0 }}
                        />
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold">{product.name}</h6>
                          <div className="small text-muted-soft mb-1">
                            <i className="bi bi-tag me-1"></i>
                            {product.category?.name || 'N/A'}
                          </div>
                          <div className="small text-muted-soft mb-1">
                            <i className="bi bi-currency-dollar me-1"></i>
                            BDT {product.price.toFixed(2)}
                          </div>
                          <div className="small text-muted-soft mb-2">
                            <i className="bi bi-box me-1"></i>
                            Stock: {product.stock_quantity ?? 'N/A'}
                          </div>
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {product.available_sizes.map((size) => (
                              <span key={size} className="badge bg-secondary">
                                {size}
                              </span>
                            ))}
                          </div>
                          <span className={`badge ${getStatusBadgeClass(product.is_active)} badge-status`}>
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary flex-fill"
                          onClick={() => navigate(`/dashboard/admin/products/${product.id}/edit`)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger flex-fill"
                          onClick={() =>
                            setDeleteModal({
                              show: true,
                              productId: product.id,
                              productName: product.name,
                            })
                          }
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(page)}>
                            {page}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <DeleteProductModal
        show={deleteModal.show}
        productName={deleteModal.productName}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ show: false, productId: null, productName: '' })}
      />
    </div>
  );
};

export default AdminProductsPage;
